import { zodResolver } from '@hookform/resolvers/zod'
import { useAtomValue } from 'jotai'
import { useResetAtom } from 'jotai/utils'
import React from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { ExecuteCancelDialog } from '~/components/elements/ExecuteCancelDialog'
import { LabelingContainer } from '~/components/elements/LabelingContainer'
import { StyledInput } from '~/components/elements/StyledInput'
import { StyledTextArea } from '~/components/elements/StyledTextArea'
import { VStack } from '~/components/elements/VStack'
import { useFormField } from '~/hooks/useFormField'
import { useLogEvent } from '~/hooks/useLogEvent'
import { ensureExists } from '~/utils'

import { dialogInfoAtom } from '../atoms'
import { useInvalidateDocumentImagesInfiniteQuery } from '../hooks/useDocumentImagesInfiniteQuery'
import { useUpdateDocumentImageMutation } from '../hooks/useUpdateDocumentImageMutation'

const schema = z.object({ comment: z.string(), title: z.string().min(1) })
type InputValues = z.infer<typeof schema>
const resolver = zodResolver(schema)

export const EditDocumentImageDialog = () => {
  const { t } = useTranslation()
  const dialogInfo = useAtomValue(dialogInfoAtom)
  const { documentImage } = ensureExists(dialogInfo, t('assertion_failed'))

  const {
    control,
    formState: { isDirty, isValid },
    handleSubmit,
  } = useForm<InputValues>({
    defaultValues: {
      comment: documentImage.comment ?? '',
      title: documentImage.title,
    },
    resolver,
  })
  const commentField = useFormField(control, 'comment')
  const titleField = useFormField(control, 'title')

  const invalidateDocumentImagesInfiniteQuery =
    useInvalidateDocumentImagesInfiniteQuery()
  const resetDialogInfo = useResetAtom(dialogInfoAtom)
  const updateDocumentImageMutation = useUpdateDocumentImageMutation({
    onSuccess: () => {
      invalidateDocumentImagesInfiniteQuery()
      resetDialogInfo()
    },
  })
  const logEvent = useLogEvent()
  const handleValid = React.useCallback<SubmitHandler<InputValues>>(
    ({ comment, title }) => {
      if (!isDirty) {
        resetDialogInfo()

        return
      }

      updateDocumentImageMutation.mutate({
        comment: comment.trim() || null,
        documentImageId: documentImage.id,
        title,
      })
      logEvent('Update document image')
    },
    [
      documentImage.id,
      isDirty,
      logEvent,
      resetDialogInfo,
      updateDocumentImageMutation,
    ],
  )

  return (
    <ExecuteCancelDialog
      disabledExecute={!isValid}
      executing={updateDocumentImageMutation.isPending}
      mode="update"
      title={t('update_tablegraph')}
      onCancel={resetDialogInfo}
      onExecute={handleSubmit(handleValid)}
    >
      <VStack spacing={4}>
        <LabelingContainer label={t('tablegraph_name')}>
          <StyledInput autoFocus={true} width="100%" {...titleField} />
        </LabelingContainer>
        <LabelingContainer label={t('comment')}>
          <StyledTextArea rows={3} width="100%" {...commentField} />
        </LabelingContainer>
      </VStack>
    </ExecuteCancelDialog>
  )
}
